import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAttachmentAndAssetHandling } from "./useAttachmentAndAssetHandling";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import {
  SAVE_PAYMENT,
  UPDATE_PAYMENT_DETAILS,
  GET_PAYMENT,
  APPROVE_PAYMENT,
  REJECT_PAYMENT,
  CANCEL_PAYMENT,
  MARK_AS_PAID,
} from "graphql/payment";
import {
  buildSavePaymentInput,
  formatPaymentForFormik,
  FormikPayment,
  paymentInitialValues,
} from "utils/payments/paymentFormat";
import { generatePaymentValidationSchema } from "validations/paymentValidations";
import PaymentStatus from "enums/PaymentStatus";
import useCustomFieldRenderer from "./useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { isEqual } from "lodash";

type usePaymentActionsProps = {
  paymentId?: string;
  shouldFetchPayments?: boolean;
};

export const usePaymentActions = ({
  paymentId,
  shouldFetchPayments = true,
}: usePaymentActionsProps) => {
  const { t } = useTranslation();
  const [selectedContactId, setSelectedContactId] = useState<
    string | undefined
  >(undefined);

  const {
    attachments,
    isLoadingAttachments,
    isUploadingAttachments,
    handleAddAttachments,
    handleUpdateAttachments,
    refetchAttachments,
  } = useAttachmentAndAssetHandling({
    parentDetails: {
      parentId: paymentId || "",
      parentType: ATTACHMENT_PARENT_TYPE.PAYMENT,
    },
    shouldFetchAttachments: !!paymentId && shouldFetchPayments,
  });

  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
    transformCustomFieldsToRecord,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.PAYMENT,
    inheritFrom:
      !paymentId && selectedContactId
        ? {
            type: CustomFieldParentType.CONTACT,
            id: selectedContactId,
          }
        : undefined,
  });

  const validationSchema = generatePaymentValidationSchema(customFieldSettings);

  const formikPayment = useFormik<FormikPayment>({
    initialValues: paymentInitialValues,
    validationSchema,
    onSubmit: async () => {},
  });

  useEffect(() => {
    const contactId = formikPayment.values?.payTo?.id;
    if (contactId !== selectedContactId) {
      setSelectedContactId(contactId);
    }
  }, [formikPayment.values?.payTo?.id, selectedContactId]);

  const {
    data: paymentData,
    loading: loadingPayment,
    refetch: refetchPayment,
  } = useQuery(GET_PAYMENT, {
    variables: { id: paymentId },
    skip: !paymentId || !shouldFetchPayments,
    fetchPolicy: "cache-and-network",
  });

  const attachmentsString = JSON.stringify(attachments);

  useEffect(() => {
    if (paymentData?.payment && paymentId) {
      const newInitialValues: FormikPayment = formatPaymentForFormik(
        paymentData.payment,
        transformCustomFieldsToRecord,
        customFieldSettings,
      );

      newInitialValues.attachments = attachments;
      formikPayment.resetForm({ values: newInitialValues });
    } else if (!paymentId && customFieldsInitialValues) {
      const currentCustomFields = formikPayment.values.customFields;

      if (!isEqual(currentCustomFields, customFieldsInitialValues)) {
        formikPayment.setFieldValue("customFields", customFieldsInitialValues);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentId,
    paymentData?.payment,
    attachmentsString,
    customFieldSettings,
    customFieldsInitialValues,
  ]);

  const [approvePayment, { loading: approveLoading }] = useMutation(
    APPROVE_PAYMENT,
    {
      onCompleted: (approvedPayment) => {
        toast.success(t("Payment approved successfully!"));
        handlePaymentResultToast({
          status: approvedPayment.approvePayment?.status,
          errorMessage: approvedPayment.approvePayment?.errorMessage,
          t,
          approvalMessage: true,
        });
      },
      onError: (err) => {
        toast.error(err.message || t("Failed to approve payment"));
      },
    },
  );

  const [rejectPayment, { loading: rejectLoading }] = useMutation(
    REJECT_PAYMENT,
    {
      onCompleted: () => {
        toast.success(t("Payment rejected successfully!"));
      },
      onError: (err) => {
        toast.error(err.message || t("Failed to reject payment"));
      },
    },
  );

  const [cancelPayment, { loading: cancelLoading }] = useMutation(
    CANCEL_PAYMENT,
    {
      onCompleted: () => {
        toast.success(t("Payment cancelled successfully!"));
      },
      onError: (err) => {
        toast.error(err.message || t("Failed to cancel payment"));
      },
    },
  );

  const payPaymentNow = async ({ id }: { id: string }) => {
    try {
      const paymentData = { ...formikPayment.values };
      paymentData.scheduledPaymentDate = new Date();

      const input = buildSavePaymentInput(
        paymentData,
        transformCustomFieldsToArray,
      );
      const savedPayment = await savePayment({ variables: { id, input } });

      const status = savedPayment?.data.savePayment?.status;
      const errorMessage = savedPayment?.data.savePayment?.errorMessage;

      handlePaymentResultToast({
        status,
        errorMessage,
        t,
      });
    } catch (err: any) {
      toast.error(err.message || t("Failed to send payment"));
      throw err;
    }
  };

  const [markAsPaid, { loading: markingAsPaid }] = useMutation(MARK_AS_PAID, {
    onCompleted: () => {
      toast.success(t("Payment marked as paid successfully!"));
    },
    onError: (err) => {
      toast.error(err.message || t("Failed to mark payment as paid"));
    },
  });

  const [savePayment, { loading: savingPayment }] = useMutation(SAVE_PAYMENT);

  const [updatePaymentDetails, { loading: savingClassification }] = useMutation(
    UPDATE_PAYMENT_DETAILS,
  );

  const workflowRequest = paymentData?.payment?.workflowRequest || null;

  // fun note in here : save payment will attempt to both save the payment and pay the payment potentially.
  // so we have to handle both scenarios where a payment could be saved properly but fail to be paid; and
  // where the payment could fail to be saved at all.
  const handleSavePayment = async (
    overrideStatus?: PaymentStatus,
  ): Promise<string | undefined> => {
    const paymentData = { ...formikPayment.values };
    if (overrideStatus) paymentData.status = overrideStatus;
    if (!overrideStatus) paymentData.status = undefined;

    let paymentId: string | undefined;
    let savedStatus: PaymentStatus | undefined;
    let errorInPayPayment: string | undefined;

    try {
      const input = buildSavePaymentInput(
        paymentData,
        transformCustomFieldsToArray,
      );
      const savedPayment = await savePayment({
        variables: { id: paymentData.id, input },
      });

      paymentId = savedPayment?.data.savePayment?.id || "";
      savedStatus = savedPayment?.data.savePayment?.status;
      errorInPayPayment = savedPayment?.data.savePayment?.errorMessage;
    } catch (error: any) {
      toast.error(t("Error saving payment") + ": " + error.message);
      console.error("Error saving payment:", error);
      return undefined;
    }

    try {
      const attachmentList = formikPayment.values?.attachments || [];

      if (attachmentList && attachmentList.length > 0 && paymentId) {
        if (paymentData.id) {
          await handleUpdateAttachments(attachmentList, {
            parentId: paymentId,
            parentType: ATTACHMENT_PARENT_TYPE.PAYMENT,
          });
        } else {
          await handleAddAttachments(attachmentList, {
            parentId: paymentId,
            parentType: ATTACHMENT_PARENT_TYPE.PAYMENT,
          });
        }
        refetchAttachments();
      }
    } catch (error: any) {
      toast.error(t("Error saving attachments") + ": " + error.message);
      console.error("Error saving attachments:", error);
    }

    handlePaymentResultToast({
      status: savedStatus,
      errorMessage: errorInPayPayment,
      t,
    });

    return paymentId;
  };

  // Single safe save for the payment details drawer's classification fields
  // (expense category, allocation, custom fields). Routes through the dedicated
  // updatePaymentDetails mutation, which never re-derives status or
  // triggers a payment, so it is safe even on a payment that has already been
  // sent. This intentionally does NOT call savePayment.
  const handleSaveClassification = async (): Promise<void> => {
    try {
      if (!paymentId) {
        throw new Error(
          "handleSaveClassification requires a paymentId; classification can only be saved on an existing payment",
        );
      }
      await updatePaymentDetails({
        variables: {
          id: paymentId,
          input: {
            expenseCategoryId: formikPayment.values.expenseCategory?.id ?? null,
            allocationCodeId: formikPayment.values.allocationCode?.id ?? null,
            customFields: transformCustomFieldsToArray(
              formikPayment.values.customFields,
            ),
          },
        },
      });
      toast.success(t("Payment details saved successfully!"));
    } catch (error: any) {
      toast.error(t("Error saving payment details"));
      console.error("Error saving payment details", error);
      throw error;
    }
  };

  const resetFormWithPayment = (payment: Partial<FormikPayment>) => {
    const paymentWithCustomFields = {
      ...payment,
      customFields: payment.customFields || customFieldsInitialValues,
    };
    formikPayment.resetForm({
      values: paymentWithCustomFields as FormikPayment,
    });
  };

  const specificLoadingStates: PaymentLoadingStates = {
    loadingPayment,
    savingPayment,
    savingClassification,
    cancelLoading,
    approveLoading,
    rejectLoading,
    isLoadingAttachments,
    isUploadingAttachments,
    markingAsPaid,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formikPayment,
    customFieldSettings,
    workflowRequest,
    isAnyLoading,
    specificLoadingStates,
    paymentInitialValues,
    refetchPayment,
    handleSavePayment,
    handleSaveClassification,
    approvePayment,
    rejectPayment,
    cancelPayment,
    payPaymentNow,
    markAsPaid,
    resetFormWithPayment,
  };
};

function handlePaymentResultToast({
  status,
  errorMessage,
  t,
  approvalMessage = false,
}: {
  status?: PaymentStatus;
  errorMessage?: string;
  t: (key: string) => string;
  approvalMessage?: boolean;
}) {
  let paymentFailMessage = approvalMessage
    ? t("Payment could not be processed")
    : t("Payment saved, but could not be processed");
  if (
    status === PaymentStatus.PAYMENT_FAILED ||
    status === PaymentStatus.INSUFFICIENT_FUNDS
  ) {
    toast.error(paymentFailMessage + (errorMessage ? ` ${errorMessage}` : ""));
    return false;
  } else if (status === PaymentStatus.PROCESSING_PAYMENT) {
    toast.success(t("Payment sent successfully!"));
    return true;
  } else if (!approvalMessage) {
    toast.success(t("Payment saved successfully!"));
    return true;
  }
}

export interface PaymentLoadingStates {
  loadingPayment: boolean;
  savingPayment: boolean;
  savingClassification: boolean;
  cancelLoading: boolean;
  approveLoading: boolean;
  rejectLoading: boolean;
  isLoadingAttachments: boolean;
  isUploadingAttachments: boolean;
  markingAsPaid: boolean;
}
